import Fetcher from 'fetchr';
import type { ParsedUrlQueryInput } from 'querystring';
import { hotelOfferService } from '@/api/hotelOfferService';
import { hotelService } from '@/api/hotelService';
import { offerInfoService } from '@/api/offerInfoService';
import { offerService } from '@/api/offerService';
import type { FetchrData } from '@/interfaces';
import { ServiceNames } from './serviceNames';

if (typeof window === 'undefined') {
    // TODO check if this can be done in a server-only place
    Fetcher.registerService(offerService);
    Fetcher.registerService(hotelService);
    Fetcher.registerService(hotelOfferService);
    Fetcher.registerService(offerInfoService);
}

export interface ClientsideErrorObject {
    output: { message: string };
    meta: Record<string, unknown>;
}

export interface ServersideErrorObject {
    // this is only a portion of the actual received object
    status: number;
}

const isClientError = (error: ClientsideErrorObject | ServersideErrorObject | unknown): error is ClientsideErrorObject =>
    (error as ClientsideErrorObject).output !== undefined;

const isServerError = (error: ClientsideErrorObject | ServersideErrorObject | unknown): error is ServersideErrorObject =>
    (error as ServersideErrorObject).status !== undefined;

export const fetchrErrorHandler = (error: ClientsideErrorObject | ServersideErrorObject | unknown): FetchrData => {
    let errorCode: number;
    if (isClientError(error)) {
        // client side error doesn't contain status code, only message...
        switch (error.output?.message) {
            case 'Not Modified':
                errorCode = 304;
                break;
            case 'Not Found':
                errorCode = 404;
                break;
            default:
                errorCode = 500;
        }
    } else if (isServerError(error)) {
        errorCode = error.status;
    } else {
        errorCode = 0; // errors that don't contain any errorcode data ends up here
    }

    return { statusCode: errorCode };
};

export const fetchService =
    (service: ServiceNames) =>
    async (apiUrl: string, queryParams: ParsedUrlQueryInput = {}, request?: Request): Promise<FetchrData> => {
        const fetcher = new Fetcher({
            xhrPath: '/reise/pakkereiser/api',
            req: request,
        });

        try {
            const { data } = await fetcher.read(service).params(queryParams).clientConfig({ apiUrl }).end();
            return { statusCode: 200, data };
        } catch (error) {
            console.log(error);
            return fetchrErrorHandler(error);
        }
    };

export const fetchOffers = fetchService(ServiceNames.OFFER_SERVICE);

export const fetchOffer = fetchService(ServiceNames.OFFER_INFO_SERVICE);

export const fetchOffersForHotel = fetchService(ServiceNames.HOTEL_OFFER_SERVICE);

export const fetchHotel = fetchService(ServiceNames.HOTEL_SERVICE);
